import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { ENTITY_INVITE_DIRECTOR_MODAL_DESCRIPTION, ENTITY_INVITE_DIRECTOR_MODAL_TITLE } from 'constants/entities';
import { INVESTOR_PAGE_TABS_IDS, INVESTORS_TYPE } from 'constants/investors';
import { ConstraintVariants } from 'constants/shared';
import { ButtonVariants } from 'constants/shared/button';
import { TypographyVariants } from 'constants/shared/typography';
import useModal from 'hooks/use-modal/useModal';
import { TabItem } from 'interfaces';
import { selectIsAdmin } from 'modules/current-user/selectors';
import { getInvestor, getInvestorFundsShares } from 'modules/investors/action';
import { selectInvestor, selectInvestorsFundsShares } from 'modules/investors/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import {
  DashboardLayout,
  InvestorInvoices,
  InvestorDeals,
  UserPageSkeleton,
  EntityInviteDirectorForm,
} from 'page-components';
import EntityInvestorsList from 'page-components/entities/EntityInvestorsList';
import { Button, Constraint, ModalWindow, Tabs } from 'shared-components';

import InvestorInfo from '../page-components/investors/InvestorInfo';
import InvestorInvestments from '../page-components/investors/InvestorInvestments';

const INVESTOR_PAGE_TABS_FM: TabItem[] = [
  { id: INVESTOR_PAGE_TABS_IDS.INVESTMENTS, name: 'Deals', component: InvestorDeals },
  { id: INVESTOR_PAGE_TABS_IDS.DEALS, name: 'Investments', component: InvestorInvestments },
];
const Investor = () => {
  const { id } = useParams();

  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);
  const { data: investor, isLoading } = useAppSelector(selectInvestor);
  const { list: fundShares } = useAppSelector(selectInvestorsFundsShares);

  const isEntity = investor?.type === INVESTORS_TYPE.ENTITY.toLocaleLowerCase() ? true : false;
  const [daxiaFundShares, setDaxiaFundShares] = useState<number | null>();

  const INVESTOR_PAGE_TABS_ADMIN: TabItem[] = [
    { id: INVESTOR_PAGE_TABS_IDS.INVESTMENTS, name: 'Deals', component: InvestorDeals },
    { id: INVESTOR_PAGE_TABS_IDS.DEALS, name: 'Investments', component: InvestorInvestments },
    { id: INVESTOR_PAGE_TABS_IDS.INVOICES, name: 'Invoices', component: InvestorInvoices },
    ...(isEntity ? [{ id: INVESTOR_PAGE_TABS_IDS.INVESTORS, name: 'Investors', component: EntityInvestorsList }] : []),
  ];

  const {
    onOpen: onOpenInvitePreviewModal,
    onClose: onCloseInvitePreviewModal,
    ...invitePreviewModalState
  } = useModal();

  useEffect(() => {
    if (id) {
      dispatch(getInvestor({ id, isAdmin }));
      dispatch(getInvestorFundsShares({ id, isAdmin, page: 1, per_page: 1000 }));
    }
  }, [dispatch, id, isAdmin]);

  useEffect(() => {
    if (fundShares?.length) {
      setDaxiaFundShares(fundShares?.find(({ fundName }) => fundName === 'DAXIA LIMITED')?.totalShares);
    }
  }, [fundShares]);

  return (
    <DashboardLayout>
      {isEntity && investor?.entityCompany && (
        <ModalWindow
          title={ENTITY_INVITE_DIRECTOR_MODAL_TITLE}
          description={ENTITY_INVITE_DIRECTOR_MODAL_DESCRIPTION}
          titleVariant={TypographyVariants.MEDIUM}
          headerClassName='text-blue-800'
          className='min-w-[70%] 2xl:min-w-[1048px]'
          contentClassName='overflow-x-hidden'
          onClose={onCloseInvitePreviewModal}
          {...invitePreviewModalState}
        >
          <EntityInviteDirectorForm
            onModalClose={onCloseInvitePreviewModal}
            entityCompanyId={investor.entityCompany.id}
          />
        </ModalWindow>
      )}
      <Constraint variant={ConstraintVariants.FULL_ROUNDED}>
        <div className='flex flex-col sm:flex-row justify-between w-full items-start mb-4'>
          {isLoading ? <UserPageSkeleton /> : <InvestorInfo investor={investor} daxiaFundShares={daxiaFundShares} />}
          {isEntity && (
            <Button className='w-40 mt-4' onClick={onOpenInvitePreviewModal} variant={ButtonVariants.SECONDARY}>
              Add to Entity
            </Button>
          )}
        </div>
        <Tabs
          tabs={isAdmin ? INVESTOR_PAGE_TABS_ADMIN : INVESTOR_PAGE_TABS_FM}
          locationState={'investor'}
          componentProps={{
            investor: true,
            companyId: investor?.entityCompany?.id,
          }}
        />
      </Constraint>
    </DashboardLayout>
  );
};

export default Investor;
